import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { FieldLayout } from "./FieldLayout";
import { filledFormService } from "@/features/filledForm/filledFormService";

interface UploadFieldProps {
  value: any[];
  onChange: (value: any[]) => void;
  label: string;
  description?: string;
  hasErrors?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  videoUrl?: string;
  multiline?: boolean;
  processId: string;
  phaseFormId: string;
  formFieldId?: string;
}

const UploadPreview = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: '1px solid #e0e0e0',
}));

export interface UploadFile {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  path?: string;
  file?: File;
}

export const UploadField: React.FC<UploadFieldProps> = ({
  value = [],
  onChange,
  label,
  description,
  hasErrors,
  required,
  error,
  helperText,
  videoUrl,
  multiline = false,
  processId,
  phaseFormId,
  formFieldId,
}) => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>(value);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // ID do campo do formulário - usa o fornecido ou extrai do label se não fornecido
  const fieldId = formFieldId || label?.toLowerCase().replace(/\s+/g, '_') || 'upload_field';

  // Sincronizar com o valor do campo atual. Antes só atualizava quando length > 0,
  // então ao ir para outro campo UPLOAD o React reutilizava a instância e a lista
  // do campo anterior ficava na tela.
  useEffect(() => {
    setUploadFiles(Array.isArray(value) ? value : []);
  }, [value, formFieldId]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Se não for multiline, limitar a apenas um arquivo
    if (!multiline && (files.length > 1 || uploadFiles.length > 0)) {
      // Permitir apenas substituir o arquivo existente
      if (uploadFiles.length > 0) {
        setUploadFiles([]);
      }
      // E usar apenas o primeiro arquivo selecionado
      const filesToProcess = [files[0]];
      processFiles(filesToProcess);
    } else {
      processFiles(Array.from(files));
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const processFiles = async (files: File[]) => {
    // Criar objetos de upload para os arquivos
    const newUploadFiles = files.map(file => ({
      id: Math.random().toString(36).substring(2, 11),
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: "uploading" as const,
      file: file
    }));
    
    // Atualizar estado e notificar mudança para o componente pai
    const updatedUploadFiles = multiline 
      ? [...uploadFiles, ...newUploadFiles] 
      : newUploadFiles;
    
    setUploadFiles(updatedUploadFiles);
    onChange(updatedUploadFiles);
    
    // Iniciar upload de cada arquivo usando o serviço
    const uploadPromises = newUploadFiles.map(async (uploadFile) => {
      try {
        // Usar o filledFormService com os parâmetros corretos
        const { path } = await filledFormService.uploadDocument(
          phaseFormId,
          processId,
          fieldId,
          uploadFile.file as File,
          (progress) => {
            // Atualizar progresso durante upload
            setUploadFiles(prev => {
              const updated = prev.map(f =>
                f.id === uploadFile.id
                  ? { ...f, progress }
                  : f
              );
              onChange(updated);
              return updated;
            });
          }
        );
        
        // Atualizar status quando upload concluído
        setUploadFiles(prev => {
          const updated = prev.map(f =>
            f.id === uploadFile.id
              ? { ...f, status: "completed" as const, path, progress: 100 }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        return path;
      } catch (error) {
        // Atualizar status em caso de erro
        setUploadFiles(prev => {
          const updated = prev.map(f =>
            f.id === uploadFile.id
              ? { ...f, status: "error" as const }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        console.error("Erro ao fazer upload do arquivo:", error);
        return null;
      }
    });
    
    await Promise.all(uploadPromises);
  };
  
  const handleRemoveFile = (id: string) => {
    const updatedFiles = uploadFiles.filter(file => file.id !== id);
    setUploadFiles(updatedFiles);
    onChange(updatedFiles);
  };

  return (
    <FieldLayout
      label={label}
      description={description}
      error={error}
      helperText={helperText}
      videoUrl={videoUrl}
    >
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {multiline 
            ? "Adicione os arquivos relacionados a este campo" 
            : "Adicione um arquivo relacionado a este campo"}
        </Typography>
        
        {/* Input de arquivo oculto */}
        <input
          type="file"
          multiple={multiline}
          onChange={handleFileSelect}
          style={{ display: "none" }}
          ref={fileInputRef}
          id={`upload-file-input-${fieldId}`}
        />
        
        <label htmlFor={`upload-file-input-${fieldId}`}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadFileIcon />}
            disabled={!multiline && uploadFiles.length > 0}
            sx={{ mt: 2, mb: 2 }}
            component="span"
          >
            {multiline ? "Adicionar Arquivos" : "Selecionar Arquivo"}
          </Button>
        </label>
        
        {/* Exibir mensagem informativa sobre os arquivos */}
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, mb: 2 }}>
          {multiline 
            ? "Você pode adicionar múltiplos arquivos." 
            : "Você pode adicionar apenas um arquivo."}
        </Typography>
        
        {/* Exibir arquivos em upload ou já carregados */}
        {uploadFiles.length > 0 ? (
          uploadFiles.map((uploadFile) => (
            <UploadPreview key={uploadFile.id}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <DescriptionIcon fontSize="large" color="primary" />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="subtitle1">{uploadFile.fileName}</Typography>
                    <Typography variant="body2">
                      Tamanho: {(uploadFile.fileSize / 1024).toFixed(2)} KB
                    </Typography>
                    
                    {/* Status do upload */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Chip 
                        label={
                          uploadFile.status === 'uploading' ? `Enviando (${Math.round(uploadFile.progress)}%)` :
                          uploadFile.status === 'completed' ? 'Enviado' : 'Erro'
                        }
                        color={
                          uploadFile.status === 'uploading' ? 'primary' :
                          uploadFile.status === 'completed' ? 'success' : 'error'
                        }
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    </Box>
                    
                    {/* Barra de progresso se estiver enviando */}
                    {uploadFile.status === 'uploading' && (
                      <Box sx={{ width: '100%', mt: 1 }}>
                        <div style={{ 
                          height: '4px', 
                          width: `${uploadFile.progress}%`,
                          backgroundColor: '#2196f3',
                          borderRadius: '2px'
                        }} />
                      </Box>
                    )}
                  </Grid>
                  <Grid item>
                    <IconButton 
                      aria-label="delete" 
                      onClick={() => handleRemoveFile(uploadFile.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </UploadPreview>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Nenhum arquivo adicionado ainda
          </Typography>
        )}
      </Box>
    </FieldLayout>
  );
}; 